import React from 'react';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { LinearProgress, Grid } from '@material-ui/core';
import Helmet from 'react-helmet';
import { reduxForm } from 'redux-form/immutable';
import { compose } from 'redux';

import Box from 'retired/Box';
import PageHeaderRedesign from 'retired/PageHeaderRedesign';
import { useUpdateEmployeeAffiliationMutation } from 'generated/graphql';

import { getAffiliationEnum } from './getAffiliationEnum';
import { getBoardPositionEnum } from './getBoardPositionEnum';
import { AffiliationsForm } from './AffiliationsForm';
import { withInitialValues } from './withInitialValues';
import { submitAction, submitFailedAction, submitSuccessAction } from './actions';
import { EDIT_AFFILIATION_FORM_IDENTIFIER } from './constants';
import { selectLoading } from './selectors';

const EditAffiliation = ({
  affiliationId,
  userId,
  loading: passedLoading,
  initialLocation,
  form,
  submitting,
}: {
  affiliationId: string;
  userId: string;
  loading: boolean;
  initialLocation: string;
  form: string;
  submitting: boolean;
}): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const reduxLoading = useSelector(selectLoading);

  const [updateEmployeeAffiliationMutation, { loading: updateLoading }] =
    useUpdateEmployeeAffiliationMutation({
      onCompleted: (data) => {
        dispatch(submitSuccessAction(userId, data));
      },
      onError: (error) => {
        dispatch(submitFailedAction(error));
      },
    });
  const loading: boolean = passedLoading || reduxLoading || updateLoading;

  const submit = (formData: Record<string, unknown>): void => {
    dispatch(submitAction(userId, formData));
    const updateData = formData.toObject();

    updateEmployeeAffiliationMutation({
      variables: {
        updateEmployeeAffiliationId: affiliationId,
        data: {
          affiliationType: getAffiliationEnum(updateData.affiliationType),
          boardPosition: getBoardPositionEnum(updateData.boardPosition),
          startingAt: updateData.startingAt,
          endingAt: updateData.endingAt,
        },
      },
    });
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Helmet title="Edit Employee Affiliations to Nonprofits" />
        <PageHeaderRedesign
          title="Edit Employee Affiliations to Nonprofits"
          onArrowClick={(): void =>
            history.length > 0 ? history.goBack() : history.push(`/volunteers/${userId}`)
          }
        />
      </Grid>
      <Grid item xs={9}>
        <AffiliationsForm
          initialLocation={initialLocation}
          submit={submit}
          submitting={submitting || loading}
          form={form}
        />
      </Grid>
      <Grid item xs={3}>
        <Box
          style={{
            backgroundColor: 'unset',
          }}
        >
          Edit Nonprofit Affiliations on behalf of this employee. Affiliations will be visible to
          the employee on their profile where they will be able to make updates
        </Box>
      </Grid>
    </Grid>
  );
};

const withForm = reduxForm({
  form: EDIT_AFFILIATION_FORM_IDENTIFIER,
  enableReinitialize: true,
});

export default compose(withInitialValues, withForm)(EditAffiliation);
